<!--
  @component
  A row or tile in the asset list, shared by repository folders and external locations. The caller
  provides the asset’s display properties and handles selection, focus and preview.
-->
<script>
  import { _ } from '@sveltia/i18n';
  import { Checkbox, GridCell, GridRow, TruncatedText } from '@sveltia/ui';

  import AssetPreview from '$lib/components/assets/shared/asset-preview.svelte';
  import { env, opensOnClick } from '$lib/services/user/env.svelte';

  /**
   * @import { Snippet } from 'svelte';
   * @import { Asset, AssetKind, ViewType } from '$lib/types/private';
   */

  /**
   * @typedef {object} Props
   * @property {string} name File name.
   * @property {AssetKind} kind Asset kind.
   * @property {Asset} [asset] Repository asset, whose thumbnail is generated by the CMS.
   * @property {string} [src] Thumbnail URL for an asset on an external service.
   * @property {number} rowIndex 0-based index of the asset in the list, for `aria-rowindex`. A
   * negative index means the position is unknown, and the attribute is omitted.
   * @property {ViewType} viewType View type.
   * @property {boolean} selected Whether the asset is selected.
   * @property {boolean} canPreview Whether the asset can be opened in the details overlay.
   * @property {(selected: boolean) => void} onSelectionChange Called when the selection changes.
   * @property {() => void} onFocus Called when the row gets focus.
   * @property {() => void} onPreview Called when the asset is opened by a click on a small screen
   * or a double-click.
   * @property {Snippet} [status] Content of the status cell, shown below the title on a tile or at
   * the end of a row, e.g. a badge. The cell is left out when not given, so that the rows of a
   * list without statuses keep their layout, and wherever the title is left out, as the cell is
   * laid out relative to the title.
   */

  /** @type {Props} */
  let {
    /* eslint-disable prefer-const */
    name,
    kind,
    asset = undefined,
    src = undefined,
    rowIndex,
    viewType,
    selected,
    canPreview,
    onSelectionChange,
    onFocus,
    onPreview,
    status = undefined,
    /* eslint-enable prefer-const */
  } = $props();

  /**
   * Open the details overlay, if the asset can be previewed.
   */
  const preview = () => {
    if (canPreview) {
      onPreview();
    }
  };
</script>

<!-- @todo Add support for drag to move. -->

<GridRow
  aria-rowindex={rowIndex >= 0 ? rowIndex + 1 : undefined}
  aria-label={name}
  onChange={(event) => {
    onSelectionChange(event.detail.selected);
  }}
  onfocus={onFocus}
  onclick={(/** @type {MouseEvent} */ event) => {
    if (opensOnClick(event)) {
      preview();
    }
  }}
  ondblclick={preview}
>
  {#if !(env.isSmallScreen || env.isMediumScreen)}
    <GridCell class="checkbox">
      <Checkbox
        tabindex="-1"
        ariaLabel={_('select_item', { values: { name } })}
        checked={selected}
        onChange={({ detail: { checked } }) => {
          onSelectionChange(checked);
        }}
      />
    </GridCell>
  {/if}
  <GridCell class="image">
    <AssetPreview
      {kind}
      {asset}
      {src}
      alt={src ? name : ''}
      variant={viewType === 'list' ? 'icon' : 'tile'}
      cover={env.isSmallScreen}
      checkerboard={kind === 'image'}
    />
  </GridCell>
  {#if !env.isSmallScreen || viewType === 'list'}
    <GridCell class="title">
      <div role="none" class="label">
        <TruncatedText lines={2}>
          <bdi>{name}</bdi>
        </TruncatedText>
      </div>
    </GridCell>
    {#if status}
      <GridCell class="status">
        {@render status()}
      </GridCell>
    {/if}
  {/if}
</GridRow>

<style>
  .label {
    word-break: break-all;
  }
</style>
